/* Fabrikone Stüdyom pure calculation engine. No DOM, requests or storage.
 * Quantity units are g, ml, m and pcs; prices are integer kurus.
 * Unknown density, dose and inventory policy never become a guessed value.
 */
(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FabrikoneStudioCalc = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const EPS = 1e-8;
  const VERSION = '1.1.0';
  const RANGES = [
    { thicknessMm: 1.2, minDiameterCm: 3, maxDiameterCm: 4 },
    { thicknessMm: 1.4, minDiameterCm: 4, maxDiameterCm: 6 },
    { thicknessMm: 1.6, minDiameterCm: 6, maxDiameterCm: 8 },
    { thicknessMm: 1.8, minDiameterCm: 9, maxDiameterCm: 10 }
  ];
  function optionalNumber(value, fallback) {
    if (value === '' || value == null) return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : NaN;
  }
  function nonnegative(value, fallback = 0) {
    const n = optionalNumber(value, fallback);
    if (!Number.isFinite(n) || n < 0) throw new RangeError('Miktar negatif olmayan sonlu bir sayı olmalı.');
    return n;
  }
  function integer(value, fallback, min = 1, max = 100000) {
    const n = optionalNumber(value, fallback);
    if (!Number.isInteger(n) || n < min || n > max) throw new RangeError('Adet geçerli bir tam sayı olmalı.');
    return n;
  }
  function positive(value, fallback) {
    const n = optionalNumber(value, fallback);
    if (!Number.isFinite(n) || n <= 0) throw new RangeError('Ölçü pozitif sonlu bir sayı olmalı.');
    return n;
  }
  function wickCandidates(diameterCm) {
    const diameter = positive(diameterCm, 6);
    return RANGES.filter(r => diameter + EPS >= r.minDiameterCm && diameter - EPS <= r.maxDiameterCm)
      .map(r => ({ ...r, label: r.thicknessMm.toFixed(1).replace('.', ',') + ' mm', status: 'test_candidate',
        reason: 'Ürün açıklamasındaki başlangıç çap aralığı; wax, koku ve boya ile yanış testi gerekir.' }));
  }
  function resolveWickLength(heightCm, cutExtraCm, mode = 'ready') {
    const height = positive(heightCm, 10);
    const extra = nonnegative(cutExtraCm, 2);
    const cutCm = height + extra;
    const lengths = mode === 'wood' ? [12] : [10, 25];
    const suitable = lengths.filter(n => n + EPS >= cutCm);
    return { heightCm: height, cutExtraCm: extra, cutCm,
      heightDefaulted: heightCm === '' || heightCm == null,
      cutExtraDefaulted: cutExtraCm === '' || cutExtraCm == null,
      selectedLengthCm: mode === 'raw' ? null : suitable[0] || null,
      suitableLengthsCm: mode === 'raw' ? [] : suitable,
      needsRawAlternative: mode !== 'raw' && suitable.length === 0,
      note: '2 cm kesim payı değiştirilebilir başlangıç ayarıdır; üreticiye ait evrensel bir teknik değer değildir.' };
  }
  function estimateContainerFill(rows) {
    const densityGPerMl = 0.9;
    const details = (Array.isArray(rows) ? rows : []).map(row => {
      const count = integer(row.count, 0, 0, 100000);
      const capacityMl = row.capacityMl == null || row.capacityMl === '' ? null : positive(row.capacityMl);
      return { handle: row.handle || null, count, capacityMl, source: row.source || null,
        totalVolumeMl: capacityMl == null ? null : count * capacityMl };
    }).filter(row => row.count > 0);
    const count = details.reduce((sum, row) => sum + row.count, 0);
    if (count > 100000) throw new RangeError('Seçilen kapların toplam mum adedi 100000’i aşamaz.');
    const missingCapacity = details.filter(row => row.capacityMl == null).map(row => row.handle);
    const complete = count > 0 && missingCapacity.length === 0;
    const totalVolumeMl = complete ? details.reduce((sum, row) => sum + row.totalVolumeMl, 0) : null;
    const estimatedG = totalVolumeMl == null ? null : totalVolumeMl * densityGPerMl;
    return { status: complete ? 'estimated' : 'missing_capacity', basis: 'container_capacity_estimate', approximate: true,
      count, densityGPerMl, totalVolumeMl, waxG: estimatedG, waxKg: estimatedG == null ? null : estimatedG / 1000,
      finishedG: estimatedG, averageFillG: estimatedG == null ? null : estimatedG / count,
      rows: details, missingCapacity,
      note: 'Seçilen kapların toplam hacmi × 0,9 ile hesaplanan ortalama değerdir; gerçek dolum miktarı wax, esans, boya ve üretim koşullarına göre değişebilir.' };
  }
  function gcd(a, b) { while (b) { const c = a % b; a = b; b = c; } return a; }
  function packageLimit(p, target, size) {
    if (p.available === false) return 0;
    let limit = Math.ceil(target / size) + 1;
    if (p.inventoryPolicy === 'DENY' && p.tracked === true && Number.isFinite(p.inventoryQuantity)) {
      limit = Math.min(limit, Math.max(0, Math.floor(p.inventoryQuantity)));
    }
    if (Number.isFinite(p.maxPackages)) limit = Math.min(limit, Math.max(0, Math.floor(p.maxPackages)));
    return limit;
  }
  function planPackages(need, sourcePacks, options = {}) {
    need = nonnegative(need);
    const policy = options.policy || 'leastExcess';
    if (!['leastExcess', 'fewest', 'cheapest'].includes(policy)) throw new RangeError('Bilinmeyen paket seçimi.');
    const packs = (sourcePacks || []).filter(p => Number.isFinite(p.amount) && p.amount > 0)
      .map(p => ({ ...p, priceMinor: Number.isInteger(p.priceMinor) && p.priceMinor >= 0 ? p.priceMinor : null }));
    if (need <= EPS) return { status: 'covered_owned', amount: 0, need, excess: 0, packageCount: 0, priceMinor: 0, lines: [], stockStatus: 'not_needed' };
    if (!packs.length) return { status: 'missing_package', need, lines: [], reason: 'İhtiyacın birimiyle eşleşen ambalaj yok.' };
    const scale = 1000;
    const sizes = packs.map(p => Math.round(p.amount * scale));
    if (sizes.some(s => s < 1)) return { status: 'unsupported_precision', need, lines: [], reason: 'Ambalaj ölçüsü 0,001 birimden küçük.' };
    const step = sizes.reduce(gcd);
    const target = Math.ceil((need * scale - EPS) / step);
    const units = sizes.map(n => n / step);
    const largest = Math.max(...units);
    let cap = target + largest - 1;
    if (cap > 200000) return { status: 'calculation_limit', need, lines: [], reason: 'Paket hesabını daha küçük partilere bölün.' };
    const limits = packs.map((p, i) => packageLimit(p, target, units[i]));
    const finiteCapacity = packs.every(p => p.available === false ||
      (p.inventoryPolicy === 'DENY' && p.tracked === true && Number.isFinite(p.inventoryQuantity)));
    if (finiteCapacity) cap = Math.min(cap, limits.reduce((s, n, i) => s + n * units[i], 0));
    if (cap < target) return { status: 'insufficient_stock', need, lines: [], reason: 'Doğrulanmış satılabilir paket sayısı yetersiz.' };
    const allPricesKnown = packs.every(p => p.priceMinor != null);
    const states = new Array(cap + 1); states[0] = { count: 0, cost: 0, counts: packs.map(() => 0) };
    packs.forEach((p, i) => {
      let remaining = Math.min(limits[i], Math.floor(cap / units[i]));
      let chunk = 1;
      while (remaining > 0) {
        const take = Math.min(chunk, remaining); remaining -= take; chunk *= 2;
        const amount = units[i] * take;
        for (let total = cap; total >= amount; total--) {
          const prev = states[total - amount]; if (!prev) continue;
          const count = prev.count + take, cost = prev.cost + (p.priceMinor || 0) * take;
          const old = states[total];
          const better = !old || (policy === 'cheapest' && allPricesKnown ?
            cost < old.cost || (cost === old.cost && count < old.count) :
            count < old.count || (count === old.count && cost < old.cost));
          if (better) { const counts = prev.counts.slice(); counts[i] += take; states[total] = { count, cost, counts }; }
        }
      }
    });
    let selected = null;
    for (let total = target; total <= cap; total++) {
      const candidate = states[total]; if (!candidate) continue;
      if (!selected || (policy === 'fewest' ? candidate.count < selected.state.count ||
        (candidate.count === selected.state.count && (total < selected.total || (total === selected.total && candidate.cost < selected.state.cost))) :
        policy === 'cheapest' && allPricesKnown ? candidate.cost < selected.state.cost ||
        (candidate.cost === selected.state.cost && (total < selected.total || (total === selected.total && candidate.count < selected.state.count))) :
        total < selected.total || (total === selected.total && (candidate.count < selected.state.count ||
          (candidate.count === selected.state.count && candidate.cost < selected.state.cost))))) selected = { total, state: candidate };
    }
    if (!selected) return { status: 'insufficient_stock', need, lines: [], reason: 'Satılabilir paket birleşimi bulunamadı.' };
    const lines = selected.state.counts.map((n, i) => n > 0 ? { ...packs[i], quantity: n } : null).filter(Boolean);
    const amount = selected.total * step / scale;
    const uncertainStock = lines.some(p => p.inventoryPolicy === 'UNKNOWN' || !p.inventoryPolicy ||
      (p.inventoryPolicy !== 'CONTINUE' && !(p.tracked === true && Number.isFinite(p.inventoryQuantity))) || p.available == null);
    return { status: 'planned', policy, amount, need, excess: Math.max(0, amount - need), packageCount: selected.state.count,
      priceMinor: lines.every(p => p.priceMinor != null) ? selected.state.cost : null, lines,
      stockStatus: uncertainStock ? 'needs_refresh' : 'verified_snapshot',
      reason: policy === 'leastExcess' ? 'Önce ihtiyacı karşılayan en küçük toplam ambalaj miktarı; eşitlikte az paket ve düşük tutar.' :
        policy === 'fewest' ? 'Önce en az paket; eşitlikte en az artan miktar.' : 'Önce en düşük tutar; eşitlikte en az artan miktar.' };
  }
  function resolveProfile(selection, product, kind, issues) {
    if (!selection || selection === 'none' || selection.handle === 'none') return { selected: false, massRatio: 0, valuePerWaxG: 0, unit: kind === 'fragrance' ? 'ml' : 'g' };
    const manual = selection.profile;
    const profile = manual && (manual.verified === true || manual.userConfirmed === true) ? manual : product && product.profile;
    if (!profile || !(profile.verified === true || profile.userConfirmed === true) || !profile.basis) {
      issues.push({ code: kind + '_dose_missing', severity: 'blocked_row', material: kind, message: 'Seçilen ürünün doğrulanmış sayısal kullanım dozu eksik.' });
      return { selected: true, massRatio: null, valuePerWaxG: null, unit: kind === 'fragrance' ? 'ml' : 'g', missing: 'dose' };
    }
    const rate = optionalNumber(selection.doseValue, profile.value);
    if (!Number.isFinite(rate) || rate < 0) {
      issues.push({ code: kind + '_dose_required', severity: 'blocked_row', material: kind, message: 'Doz değeri seçilmeli veya doğrulanmalı.' });
      return { selected: true, massRatio: null, valuePerWaxG: null, unit: kind === 'fragrance' ? 'ml' : 'g', missing: 'dose_value' };
    }
    if ((Number.isFinite(profile.min) && rate < profile.min - EPS) || (Number.isFinite(profile.max) && rate > profile.max + EPS)) {
      issues.push({ code: kind + '_dose_out_of_range', severity: 'blocked_row', material: kind, message: 'Doz doğrulanmış ürün aralığının dışında.' });
      return { selected: true, massRatio: null, valuePerWaxG: null, unit: kind === 'fragrance' ? 'ml' : 'g', missing: 'dose_range' };
    }
    let perG, unit, massRatio;
    if (profile.basis === 'ml_per_100g_wax') { perG = rate / 100; unit = 'ml'; }
    else if (profile.basis === 'g_per_100g_wax' || profile.basis === 'mass_percent_wax') { perG = rate / 100; unit = 'g'; massRatio = perG; }
    else if (profile.basis === 'g_per_kg_wax') { perG = rate / 1000; unit = 'g'; massRatio = perG; }
    else {
      issues.push({ code: kind + '_basis_unknown', severity: 'blocked_row', material: kind, message: 'Kullanım dozunun birimi desteklenmiyor.' });
      return { selected: true, massRatio: null, valuePerWaxG: null, unit: null, missing: 'basis' };
    }
    if (unit === 'ml') {
      if ((profile.densityVerified === true || profile.userConfirmed === true) && Number.isFinite(profile.densityGPerMl) && profile.densityGPerMl > 0) massRatio = perG * profile.densityGPerMl;
      else { massRatio = rate === 0 ? 0 : null; if (rate > 0) issues.push({ code: kind + '_density_missing', severity: 'mass_pending', material: kind, message: 'ml dozu hesaplanır; net gram ve mum verimi için doğrulanmış yoğunluk gerekir.' }); }
    }
    if (profile.compatibilityVerified !== true && selection.compatibilityConfirmed !== true) issues.push({ code: kind + '_compatibility_pending', severity: 'review', material: kind, message: 'Bu wax ve reçete ile uyumluluğunu küçük denemeyle doğrulayın.' });
    return { selected: true, massRatio, valuePerWaxG: perG, unit, rate, basis: profile.basis,
      densityGPerMl: profile.densityGPerMl || null, densityVerified: profile.densityVerified === true || profile.userConfirmed === true,
      source: profile.source || (profile.userConfirmed ? 'Kullanıcının doğruladığı teknik bilgi' : null) };
  }
  function productFor(catalog, handle) { return (catalog && catalog.products || []).find(p => p.handle === handle) || null; }
  function packsFor(catalog, product, unit, lengthCm) {
    if (!product) return [];
    const related = product.kind === 'wax' && product.family ? (catalog.products || []).filter(p => p.kind === 'wax' && p.family === product.family) : [product];
    return related.flatMap(p => (p.variants || []).filter(v => v.pack && v.pack.unit === unit &&
      (lengthCm == null || v.pack.lengthCm === lengthCm)).map(v => ({ ...v, handle: p.handle, productTitle: p.title, amount: v.pack.amount, unit })));
  }
  function calculate(input, catalog = { products: [] }) {
    input = input || {};
    const issues = [], materials = [];
    const mode = input.mode === 'wax' || input.mode === 'availableWax' ? 'wax' : 'count';
    const countInput = input.count == null || input.count === '' ? null : integer(input.count, null);
    const netFillG = positive(input.netFillG != null ? input.netFillG : input.net, 100);
    const waxKg = mode === 'wax' ? positive(input.waxKg != null ? input.waxKg : input.availableWaxKg, 1) : null;
    const owned = input.owned || {};
    const wax = productFor(catalog, input.waxHandle || input.wax && input.wax.handle);
    const fragranceSelection = typeof input.fragrance === 'string' ? { handle: input.fragrance } : input.fragrance || { handle: input.fragranceHandle || 'none', doseValue: input.fragranceDose };
    const dyeSelection = typeof input.dye === 'string' ? { handle: input.dye } : input.dye || { handle: input.dyeHandle || 'none', doseValue: input.dyeDose };
    const fragrance = productFor(catalog, fragranceSelection.handle);
    const dye = productFor(catalog, dyeSelection.handle);
    const f = resolveProfile(fragranceSelection, fragrance, 'fragrance', issues);
    const d = resolveProfile(dyeSelection, dye, 'dye', issues);
    const canComputeMass = f.massRatio != null && d.massRatio != null;
    const factor = canComputeMass ? 1 + f.massRatio + d.massRatio : null;
    let count = countInput, waxG = null, finishedG = null, possibleCount = null, unpouredMixtureG = null;
    if (mode === 'count') {
      count = countInput == null ? 6 : countInput;
      finishedG = count * netFillG;
      if (canComputeMass) waxG = finishedG / factor;
      else issues.push({ code: 'net_fill_pending', severity: 'mass_pending', material: 'wax', message: 'Net dolumdan wax miktarı için katkıların gram karşılığı eksik. Tartılmış wax modu yine kullanılabilir.' });
    } else {
      waxG = waxKg * 1000;
      if (canComputeMass) { finishedG = waxG * factor; possibleCount = Math.floor((finishedG + EPS) / netFillG); unpouredMixtureG = finishedG - possibleCount * netFillG; }
      if (count == null) count = possibleCount;
      if (count === 0) issues.push({ code: 'yield_below_one', severity: 'review', material: 'yield', message: 'Bu dolum için tartılmış wax bir tam mum üretimine yetmiyor.' });
      if (possibleCount == null) issues.push({ code: 'yield_pending', severity: 'mass_pending', material: 'yield', message: 'Tartılmış wax ve ml esans miktarı hesaplandı; net gram bazlı mum verimi yoğunluk bilgisi bekliyor.' });
      if (count != null && possibleCount != null && count > possibleCount) issues.push({ code: 'planned_count_exceeds_yield', severity: 'review', material: 'yield', message: 'Planlanan adet bu net dolum için hesaplanan verimin üzerinde.' });
    }
    const fragranceValue = waxG != null && f.valuePerWaxG != null ? waxG * f.valuePerWaxG : null;
    const dyeG = waxG != null && d.valuePerWaxG != null ? waxG * d.valuePerWaxG : null;
    const policy = input.packPolicy || 'leastExcess';
    function row(key, product, unit, required, ownedValue, extra = {}) {
      const have = nonnegative(ownedValue);
      const need = required == null ? null : Math.max(0, required - have);
      const r = { key, handle: product && product.handle || null, title: product && product.title || extra.title || key, unit,
        required, owned: have, needToBuy: need, status: required == null ? 'pending_data' : need <= EPS ? 'covered_owned' : 'needs_product', ...extra };
      if (need != null && need > EPS && product) {
        r.packages = planPackages(need, packsFor(catalog, product, unit, extra.lengthCm), { policy });
        r.status = r.packages.status;
      } else if (need != null && need <= EPS) r.packages = planPackages(0, []);
      if (r.status === 'insufficient_stock' || r.status === 'missing_package') issues.push({ code: key + '_' + r.status, severity: 'blocked_row', material: key, message: r.packages.reason });
      materials.push(r); return r;
    }
    const ownedWaxG = mode === 'wax' && input.availableWaxOwned !== false && owned.waxG == null ? waxG : owned.waxG;
    row('wax', wax, 'g', waxG, ownedWaxG, { title: 'Mum bazı' });
    if (f.selected) {
      const soldUnit = fragrance && fragrance.variants && fragrance.variants[0] && fragrance.variants[0].pack.unit || f.unit || 'ml';
      let purchaseValue = fragranceValue;
      if (f.unit === 'g' && soldUnit === 'ml' && fragranceValue != null) {
        if (f.densityVerified && f.densityGPerMl > 0) purchaseValue = fragranceValue / f.densityGPerMl;
        else { purchaseValue = null; issues.push({ code: 'fragrance_purchase_density_pending', severity: 'blocked_row', material: 'fragrance', message: 'Gram esans tüketimi hesaplandı; ml ambalaj seçmek için doğrulanmış yoğunluk gerekir.' }); }
      }
      row('fragrance', fragrance, soldUnit, purchaseValue, soldUnit === 'g' ? owned.fragranceG : owned.fragranceMl, { source: f.source, consumptionUnit: f.unit, consumptionAmount: fragranceValue });
    }
    if (d.selected) row('dye', dye, d.unit || 'g', dyeG, owned.dyeG, { source: d.source,
      usageReview: dye && dye.dyeKind === 'metallic' ? 'Metalik boya için yanış ve reçete uygunluğu ayrıca doğrulanmalı.' : null });
    const height = resolveWickLength(input.heightCm, input.cutExtraCm, input.wickMode || 'ready');
    const diameterCm = positive(input.diameterCm, 6);
    const candidates = wickCandidates(diameterCm);
    const perCandle = integer(input.wicksPerCandle, 1, 1, 20);
    const wickCount = count == null ? null : count * perCandle;
    const wickProduct = productFor(catalog, input.wickHandle);
    const wickMode = input.wickMode || (wickProduct && wickProduct.kind === 'raw-wick' ? 'raw' : wickProduct && wickProduct.kind === 'wood-wick' ? 'wood' : 'ready');
    const length = resolveWickLength(input.heightCm, input.cutExtraCm, wickMode);
    if (wickProduct && wickProduct.thicknessMm && !candidates.some(c => Math.abs(c.thicknessMm - wickProduct.thicknessMm) < EPS)) issues.push({ code: 'wick_diameter_review', severity: 'review', material: 'wick', message: 'Seçilen kalınlık girilen iç çap için kaynak başlangıç aralığında değil; yanış testiyle doğrulayın.' });
    if (wickMode === 'raw') {
      row('wick', wickProduct, 'm', wickCount == null ? null : wickCount * length.cutCm / 100, owned.rawWickM, { cutCm: length.cutCm, title: 'Mumlanmamış pamuk fitil' });
      const clip = productFor(catalog, input.clipHandle) || (catalog.products || []).find(p => p.kind === 'clip');
      row('clip', clip, 'pcs', wickCount, owned.clips, { title: 'Metal fitil tabanı' });
    } else {
      const tooShort = !length.selectedLengthCm;
      row('wick', wickProduct, 'pcs', wickCount, owned.wicks, { lengthCm: wickMode === 'ready' ? length.selectedLengthCm : null, title: 'Hazır fitil', lengthPending: tooShort });
      if (tooShort) {
        const wr = materials.find(m => m.key === 'wick'); wr.status = 'length_not_available'; wr.packages = null;
        issues.push({ code: 'wick_length_not_available', severity: 'blocked_row', material: 'wick', message: 'Hazır fitil çalışma boyuna kısa kalıyor; daha uzun gerçek ürün veya kesilebilir ham fitil seçin.' });
      }
    }
    if (input.jarHandle || input.requiresJar !== false) row('jar', productFor(catalog, input.jarHandle), 'pcs', count, owned.jars, { title: 'Mum kabı' });
    const toolSelections = new Map((input.tools || []).map(tool => [tool.handle, tool]));
    for (const tool of toolSelections.values()) {
      const available = tool.available !== false;
      if (!available) row('tool:' + tool.handle, productFor(catalog, tool.handle), 'pcs', 1, nonnegative(tool.owned), { title: tool.title || 'Atölye ekipmanı', reusable: true });
    }
    const cartMap = new Map();
    for (const m of materials) for (const p of m.packages && m.packages.lines || []) {
      if (!p.id || !/^\d+$/.test(String(p.id))) continue;
      const old = cartMap.get(String(p.id));
      if (old) { old.quantity += p.quantity; if (!old.materialKeys.includes(m.key)) old.materialKeys.push(m.key); }
      else cartMap.set(String(p.id), { id: String(p.id), quantity: p.quantity, handle: p.handle, price: p.priceMinor, materialKeys: [m.key] });
    }
    const purchaseRows = materials.filter(m => m.needToBuy != null && m.needToBuy > EPS);
    const plannedRows = purchaseRows.filter(m => m.packages && m.packages.status === 'planned');
    const knownCostMinor = plannedRows.reduce((sum, m) => sum + (m.packages.priceMinor || 0), 0);
    const costComplete = purchaseRows.every(m => m.packages && m.packages.status === 'planned' && m.packages.priceMinor != null);
    const pendingRows = materials.filter(m => !['planned', 'covered_owned'].includes(m.status));
    const status = pendingRows.length || issues.some(i => i.severity === 'mass_pending' || i.severity === 'blocked_row') ? 'partial' : issues.some(i => i.severity === 'review') ? 'needs_review' : 'ready';
    return { version: VERSION, status, issues, materials,
      recipe: { mode, count, plannedCount: countInput, netFillG, waxG, fragrance: { ...f, amount: fragranceValue }, dye: { ...d, amount: dyeG },
        finishedG: canComputeMass ? finishedG : null, targetFinishedG: mode === 'count' ? count * netFillG : null,
        possibleCount, unpouredMixtureG, massComplete: canComputeMass },
      wicks: { ...length, wickMode, diameterCm, diameterDefaulted: input.diameterCm == null || input.diameterCm === '', perCandle, count: wickCount, candidates,
        compatibilityStatus: 'test_required', clipRequired: wickMode === 'raw' },
      totals: { knownCostMinor, purchaseCostMinor: costComplete ? knownCostMinor : null, costComplete, pendingRows: pendingRows.map(m => m.key) },
      cartLines: [...cartMap.values()], cartReady: pendingRows.length === 0 && plannedRows.every(m => m.packages.stockStatus === 'verified_snapshot'),
      stockNeedsRefresh: plannedRows.some(m => m.packages.stockStatus === 'needs_refresh') };
  }
  return { VERSION, calculate, planPackages, wickCandidates, resolveWickLength, packsFor, estimateContainerFill };
});
